// src/components/BlogContent.js
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import StickyCta from './StickyCta';

// Update the scopeStyles function
const scopeStyles = (css, scope) => {
  if (!css) return '';
  try {
    return css
      // Handle standard rules
      .replace(/([^@{\n]+)(?=\s*{)/g, (match) => 
        match.split(',').map(selector => 
          `${scope} ${selector.trim()}`
        ).join(', ')
      )
      // Handle media queries
      .replace(/@media[^{]+\{([^}]+)\}/g, (match) => 
        match.replace(/([^{]+)(?=\s*{)/g, (innerMatch) =>
          innerMatch.split(',').map(selector => 
            `${scope} ${selector.trim()}`
          ).join(', ')
        )
      );
  } catch (e) {
    //console.error('Error scoping styles:', e);
    return css;
  }
};

const BlogContent = () => {
  const { postId } = useParams();
  const [htmlContent, setHtmlContent] = useState('');
  const [headers, setHeaders] = useState([]);
  const [metadata, setMetadata] = useState({ title: '', author: '', date: '' });
  const [headStyles, setHeadStyles] = useState('');

  const htmlPath = `blog_posts/content/${postId}.html`;
  const imagePath = `blog_posts/images/${postId}/${postId}-banner.jpg`;

  const processHtml = async (rawHtml) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, 'text/html');
  
    // --- Add IDs to all header elements ---
    const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach((heading) => {
      const text = heading.textContent.trim();
      // Generate an ID: replace spaces with dashes and convert to lowercase
      const id = text.replace(/\s+/g, '-').toLowerCase();
      heading.setAttribute('id', id);
    });
  
    // --- Process images as before ---
    const images = doc.querySelectorAll('img');
    await Promise.all(
      Array.from(images).map(async (img) => {
        let filename = img.getAttribute('src') || '';
        if (filename.startsWith('images/')) {
          filename = filename.replace('images/', '');
        }
        try {
          const imageRef = ref(getStorage(), `blog_posts/images/${postId}/${filename}`);
          const url = await getDownloadURL(imageRef);
          img.setAttribute('src', url);
  
          img.removeAttribute('width');
          img.removeAttribute('height');
          img.removeAttribute('style');
  
          // Center images on mobile only, left-aligned on desktop
img.classList.add(
  'w-full', 'max-w-none', 'h-auto', 'rounded-lg', 'my-4'
);
        } catch (error) {
          //console.error('Error loading image:', error);
          img.remove();
        }
      })
    );
  
    // --- Remove inline styles from all elements ---
    doc.querySelectorAll('*').forEach(el => {
      el.removeAttribute('style');
    });
  
    // --- Gather any styles from the head ---
    const headStyles = Array.from(doc.head.querySelectorAll('style'))
      .map((el) => el.outerHTML)
      .join('\n');
  
    return {
      styles: headStyles,
      body: doc.body.innerHTML,
    };
  };

  const extractHeaders = (htmlString) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      const headingElements = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
      setHeaders(Array.from(headingElements).map((el) => el.textContent.trim()));
    } catch (error) {
      //console.error('Error parsing HTML for headers:', error);
    }
  };

  const fetchHtmlContent = useCallback(async () => {
    try {
      const htmlRef = ref(getStorage(), htmlPath);
      const url = await getDownloadURL(htmlRef);
      const response = await fetch(url);
      const rawHtml = await response.text();

      const { styles, body } = await processHtml(rawHtml);
      setHtmlContent(body);
      setHeadStyles(styles);
      extractHeaders(body);
    } catch (error) {
      //console.error('Error fetching HTML content:', error);
    }
  }, [htmlPath, postId]);

  const fetchMetadata = useCallback(async () => {
    try {
      const db = getFirestore();
      const docRef = doc(db, 'blogPosts', postId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setMetadata(docSnap.data());
      }
    } catch (error) {
      //console.error('Error fetching metadata:', error);
    }
  }, [postId]);

  useEffect(() => {
    fetchHtmlContent();
    fetchMetadata();
  }, [fetchHtmlContent, fetchMetadata]);

  const onHeaderClick = (headerText) => {
    const id = headerText.replace(/\s+/g, '-').toLowerCase();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div>
<div className="grid grid-cols-1 md:grid-cols-5 gap-8 pb-24 lg:pb-20">
        {/* ───────── LEFT – Table of contents ───────── */}
  <aside className="
      hidden md:block md:col-span-1
      sticky top-16 z-20 self-start
      p-4 border rounded-2xl
    ">
                <h3 className="text-base font-bold mb-2">In This Article</h3>
          <ul className="space-y-1 text-sm">
            {headers.map((header, idx) => (
              <li key={idx}>
                <button onClick={() => onHeaderClick(header)} className="text-gray-700 hover:text-mm_blue text-left w-full font-light">
                  {header}
                </button>
              </li>
            ))}
          </ul>
        </aside>

  {/* ───────── CENTER – Article body ───────── */}
          <div className="md:col-span-3 order-2 md:order-none">
          <article className="bg-white rounded-lg overflow-hidden">        
          <div className="
            prose blog-content-scope isolate 
            prose-base prose-headings:font-large
            prose-headings:mb-4
            prose-h1:mt-4 prose-h2:mt-4 prose-h3:mt-4
            prose-img:w-full prose-img:max-w-none prose-img:h-auto prose-img:my-4
            prose-p:leading-relaxed prose-p:mt-0 prose-ul:list-disc
            prose-ol:list-decimal w-full max-w-full 
            break-words px-0 py-0 md:px-6 md:mb-12
            font-normal text-gray-600"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
          </article>
        </div>
{/* ───────── RIGHT – Sticky CTA ───────── */}
<div className="order-3 ">
  <div className="sticky top-16 z-20">
    <StickyCta />
  </div>
</div>

      </div>
    </div>
  );
};

export default BlogContent;